import React, { useId } from 'react';

export type InfinityColorVariant = 'cosmic-red' | 'temple-gold' | 'surya-gold';

interface LogoInfinityCursorProps {
  className?: string;
  width?: number;
  height?: number;
  variant?: InfinityColorVariant;
}

/**
 * LogoInfinityCursor - Exact Waypoint Cosmic Traversal
 * 
 * Traversal sequence explicitly defined:
 * 1. Center (24, 12)
 * 2. Right top apex (35, 3)
 * 3. Right side apex (44, 12)
 * 4. Right bottom (35, 21)
 * 5. Center (24, 12)
 * 6. Top left (13, 3)
 * 7. Left side apex (4, 12)
 * 8. Left bottom (13, 21)
 * 9. Center (24, 12)
 * 10. Seamlessly continues back to Right top (35, 3) in an endless, continuous flow.
 * 
 * Color Variants:
 * - 'temple-gold' (Default): Option A - High-Visibility 24K Vedic Temple Suvarna (Deep burnished amber & radiant gold)
 * - 'surya-gold': Option C - Surya Tejas / Fiery Solar Gold (Saffron-amber & luminous gold)
 * - 'cosmic-red': Astral vermilion red
 */
export const LogoInfinityCursor: React.FC<LogoInfinityCursorProps> = ({
  className = '',
  width = 45,
  height = 18,
  variant = 'temple-gold',
}) => {
  const uniqueId = useId().replace(/[:]/g, '');

  // Exact 2.5:1 aspect ratio path (viewBox 0 0 60 24, width=60, height=24 -> 2.5:1)
  const requestedInfinityPath = `
    M 30 12
    C 35 6.5, 37.5 3, 43.75 3
    A 11.25 9 0 0 1 55 12
    A 11.25 9 0 0 1 43.75 21
    C 37.5 21, 35 17.5, 30 12
    C 25 6.5, 22.5 3, 16.25 3
    A 11.25 9 0 0 0 5 12
    A 11.25 9 0 0 0 16.25 21
    C 22.5 21, 25 17.5, 30 12 Z
  `.replace(/\s+/g, ' ').trim();

  // Palette configuration based on selected variant
  const palette = {
    'temple-gold': {
      // Balanced Temple Suvarna (24K Gold)
      // Balanced visibility: clearly discernible against the cream capsule without being overly heavy or stark
      stops: [
        { offset: '0%', color: '#92400E' },   // Warm amber gold anchor
        { offset: '16%', color: '#B45309' },  // Polished bullion gold
        { offset: '32%', color: '#D97706' },  // Rich 24K temple gold
        { offset: '50%', color: '#F59E0B' },  // Luminous solar gold apex
        { offset: '68%', color: '#D97706' },  // Rich 24K temple gold
        { offset: '84%', color: '#B45309' },  // Polished bullion gold
        { offset: '100%', color: '#92400E' }, // Warm amber gold anchor
      ],
      glowStops: [
        { offset: '0%', color: '#B45309', opacity: 0.30 },
        { offset: '50%', color: '#F59E0B', opacity: 0.60 },
        { offset: '100%', color: '#B45309', opacity: 0.30 },
      ],
      trackStroke: '#B45309',
      trackOpacity: 0.22,
      edgeStroke: '#92400E',
      edgeOpacity: 0.32,
    },
    'surya-gold': {
      // Option C: Surya Tejas / Fiery Solar Gold (Saffron-amber & Sun Gold)
      stops: [
        { offset: '0%', color: '#9A3412' },
        { offset: '18%', color: '#C2410C' },
        { offset: '38%', color: '#EA580C' },
        { offset: '50%', color: '#F59E0B' },
        { offset: '62%', color: '#EA580C' },
        { offset: '82%', color: '#C2410C' },
        { offset: '100%', color: '#9A3412' },
      ],
      glowStops: [
        { offset: '0%', color: '#EA580C', opacity: 0.40 },
        { offset: '50%', color: '#F59E0B', opacity: 0.80 },
        { offset: '100%', color: '#EA580C', opacity: 0.40 },
      ],
      trackStroke: '#C2410C',
      trackOpacity: 0.25,
      edgeStroke: '#7C2D12',
      edgeOpacity: 0.45,
    },
    'cosmic-red': {
      stops: [
        { offset: '0%', color: '#9E1208' },
        { offset: '18%', color: '#D82315' },
        { offset: '38%', color: '#FF3626' },
        { offset: '50%', color: '#FF6B59' },
        { offset: '62%', color: '#FF3626' },
        { offset: '82%', color: '#D82315' },
        { offset: '100%', color: '#9E1208' },
      ],
      glowStops: [
        { offset: '0%', color: '#FF3326', opacity: 0.4 },
        { offset: '50%', color: '#FF7A66', opacity: 0.7 },
        { offset: '100%', color: '#FF3326', opacity: 0.4 },
      ],
      trackStroke: '#C82A1E',
      trackOpacity: 0.16,
      edgeStroke: '#7F1D1D',
      edgeOpacity: 0.35,
    },
  }[variant];

  return (
    <div 
      className={`relative inline-flex items-center justify-center select-none ${className}`}
      style={{ width, height }}
    >
      <svg
        viewBox="0 0 60 24"
        width={width}
        height={height}
        className="overflow-visible"
      >
        <defs>
          {/* Main Spectrum Gradient */}
          <linearGradient id={`infinity-spectrum-${uniqueId}`} x1="0%" y1="50%" x2="100%" y2="50%">
            {palette.stops.map((s, idx) => (
              <stop key={idx} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>

          {/* Ethereal Nebula Glow Gradient */}
          <linearGradient id={`nebula-glow-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="0%">
            {palette.glowStops.map((s, idx) => (
              <stop key={idx} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
            ))}
          </linearGradient>

          <style>{`
            /* Continuous Unbroken Flow along exact requested path:
               pathLength="100" standardizes coordinates to 0..100
               stroke-dasharray="100 100"
               0%   : offset 100  -> Head at Center (0% of path visible)
               50%  : offset 0    -> Ribbon fully drawn through Center -> Right Top -> Right Side -> Right Bottom -> Center -> Top Left -> Left -> Left Bottom -> Center (100% visible)
               100% : offset -100 -> Tail catches up following the exact same path (0% visible)
               Seamlessly repeats at offset 100 without jumping or stopping.
               Total cycle duration: 5.6s (deliberate, smooth, serene).
            */
            @keyframes waypoint-infinity-flow-${uniqueId} {
              0% {
                stroke-dashoffset: 100;
              }
              100% {
                stroke-dashoffset: -100;
              }
            }

            .infinity-waypoint-path-${uniqueId} {
              stroke-dasharray: 100 100;
              animation: waypoint-infinity-flow-${uniqueId} 5.6s linear infinite;
              will-change: stroke-dashoffset;
            }
          `}</style>
        </defs>

        <g className="overflow-visible">
          {/* Subtle Ambient Track: faint guide outline showing complete infinity path */}
          <path
            d={requestedInfinityPath}
            pathLength={100}
            fill="none"
            stroke={palette.trackStroke}
            strokeWidth="2.0"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={palette.trackOpacity}
          />

          {/* 1. Deep Nebula Aura (diffused warm glow following moving ribbon) */}
          <path
            d={requestedInfinityPath}
            pathLength={100}
            fill="none"
            stroke={`url(#nebula-glow-${uniqueId})`}
            strokeWidth="4.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.26"
            className={`blur-[1px] infinity-waypoint-path-${uniqueId}`}
          />

          {/* 2. High-Contrast Edge Under-Layer (gives sharp definition & depth on light/cream backgrounds) */}
          <path
            d={requestedInfinityPath}
            pathLength={100}
            fill="none"
            stroke={palette.edgeStroke}
            strokeWidth="3.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={palette.edgeOpacity}
            className={`infinity-waypoint-path-${uniqueId}`}
          />

          {/* 3. Primary Infinity Ribbon (Multi-Stop Spectrum Gradient) */}
          <path
            d={requestedInfinityPath}
            pathLength={100}
            fill="none"
            stroke={`url(#infinity-spectrum-${uniqueId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`infinity-waypoint-path-${uniqueId}`}
          />
        </g>
      </svg>
    </div>
  );
};
